import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { OwnStore } from '../../../state'
import type { Cashier } from '../../agent-session/model/agentSessionController'
import { createOwnCashierApi, type OwnCashierApi } from '../api/posCashierApi'
import { cashierBlockReason, cashierOfGrant, problemOf, type CashierListState, type CashierProblem, type PosCashierChoice } from './cashier'
import {
  cashierOf,
  ownCashierExpired,
  ownCashierForgotten,
  ownCashierListFailed,
  ownCashierListReady,
  ownCashierReset,
  ownCashierSelected,
  ownCashierVerified,
  ownCashierVerifyFailed,
  ownCashierVerifyStarted,
  ownCashierWindowClosed,
} from './cashierSlice'

/**
 * Кассир окна «РМК» «Собственной» (RP-OWN11, часть Б): список кассиров, выбор кассира нашей системы, его код → подтверждение (`purpose = pos`).
 *
 * Где что лежит (срок жизни `screen`): выбранный кассир и подтверждение — в слайсе `cashier` (память вкладки), не в хранилище
 * браузера и не в адресе. Код хук не хранит вовсе: он приходит аргументом `verify` и уходит в тело одного запроса; токен
 * подтверждения в слайс тоже не кладётся. Ошибка службы Cashiers остаётся на экране, пока кассир её не заменит новым действием:
 * по таймеру она не исчезает.
 */

/** Таймер браузера не принимает задержку больше 2^31-1 мс. */
const MAX_DELAY_MS = 2_147_483_647

export interface OwnCashierSession {
  list: CashierListState
  selected: PosCashierChoice | null
  /** Имя подтверждённого кассира; `null` — подтверждения нет. */
  grantedName: string | null
  /** Кассир для запросов агента (имя и `cashierId` из подтверждения); `null` — деньги закрыты. */
  cashier: Cashier | null
  /** Проверяет срок в момент вызова: команда с деньгами берёт кассира только отсюда. */
  cashierNow: () => Cashier | null
  /** Почему деньги закрыты; `null` — кассир подтверждён. */
  blockReason: string | null
  verifying: boolean
  problem: CashierProblem | null
  select: (sourceCashierId: string | null) => void
  /** Код уходит в один запрос и нигде не остаётся. `true` — кассир подтверждён. */
  verify: (pin: string) => Promise<boolean>
  reload: () => void
  /** «Сменить кассира»: подтверждение забывается. */
  forget: () => void
}

export function useOwnCashier(resourceId: string, store: OwnStore, api?: OwnCashierApi, now: () => number = Date.now): OwnCashierSession {
  const { dispatch } = store
  const state = cashierOf(store.state.cashier, resourceId)
  const storeApi = useMemo(() => createOwnCashierApi(dispatch), [dispatch])
  const calls = api ?? storeApi
  const [reloadKey, setReloadKey] = useState(0)
  const grantRef = useRef(state.grant)
  useEffect(() => {
    grantRef.current = state.grant
  }, [state.grant])
  const verifying = useRef(false)

  useEffect(() => {
    if (resourceId === '') return
    let live = true
    dispatch(ownCashierReset({ resourceId }))
    void calls.list(resourceId).then(
      (result) => {
        if (!live) return
        if (result.ok) dispatch(ownCashierListReady({ resourceId, items: result.data }))
        else dispatch(ownCashierListFailed({ resourceId, problem: problemOf(result.error, 'Чтение списка кассиров') }))
      },
      (error: unknown) => {
        if (live) dispatch(ownCashierListFailed({ resourceId, problem: problemOf(error, 'Чтение списка кассиров') }))
      },
    )
    return () => {
      live = false
    }
  }, [calls, dispatch, resourceId, reloadKey])

  // Окно закрыто: выбор и подтверждение кассира в следующее окно не переходят.
  useEffect(
    () => () => {
      dispatch(ownCashierWindowClosed({ resourceId }))
    },
    [dispatch, resourceId],
  )

  // Подтверждение живёт десять минут: по истечении оно снимается само, и следующая команда с деньгами требует код.
  const expiresAtMs = state.grant?.expiresAtMs ?? null
  useEffect(() => {
    if (expiresAtMs === null) return
    const timer = setTimeout(() => dispatch(ownCashierExpired({ resourceId })), Math.min(MAX_DELAY_MS, Math.max(0, expiresAtMs - now())))
    return () => clearTimeout(timer)
  }, [dispatch, expiresAtMs, now, resourceId])

  const selected = state.list.status === 'ready' ? (state.list.items.find((item) => item.sourceCashierId === state.selectedId) ?? null) : null

  const verify = useCallback(
    async (pin: string): Promise<boolean> => {
      if (verifying.current || selected === null || pin.trim() === '') return false
      verifying.current = true
      dispatch(ownCashierVerifyStarted({ resourceId }))
      try {
        const result = await calls.verify({ resourceId, cashierId: selected.cashierId, sourceCashierId: selected.sourceCashierId, credential: pin })
        if (!result.ok) {
          dispatch(ownCashierVerifyFailed({ resourceId, problem: problemOf(result.error, `Подтверждение кассира «${selected.name}» (код)`) }))
          return false
        }
        // Подтвердили не того, кого выбрали (ответ чужого кассира) — не принимаем: в чек пошёл бы чужой GUID.
        if (result.data.cashierId !== selected.sourceCashierId) {
          dispatch(
            ownCashierVerifyFailed({
              resourceId,
              problem: problemOf(new Error('Служба кассиров подтвердила другого кассира, не выбранного.'), `Подтверждение кассира «${selected.name}» (код)`),
            }),
          )
          return false
        }
        dispatch(ownCashierVerified({ resourceId, grant: result.data }))
        return true
      } catch (error) {
        dispatch(ownCashierVerifyFailed({ resourceId, problem: problemOf(error, `Подтверждение кассира «${selected.name}» (код)`) }))
        return false
      } finally {
        verifying.current = false
      }
    },
    [calls, dispatch, resourceId, selected],
  )

  const cashierNow = useCallback((): Cashier | null => {
    const grant = grantRef.current
    if (grant === null) return null
    if (now() >= grant.expiresAtMs) {
      dispatch(ownCashierExpired({ resourceId }))
      return null
    }
    return cashierOfGrant(grant)
  }, [dispatch, now, resourceId])

  const select = useCallback((id: string | null): void => void dispatch(ownCashierSelected({ resourceId, id })), [dispatch, resourceId])
  const reload = useCallback((): void => setReloadKey((value) => value + 1), [])
  const forget = useCallback((): void => void dispatch(ownCashierForgotten({ resourceId })), [dispatch, resourceId])
  const cashier = useMemo(() => (state.grant === null ? null : cashierOfGrant(state.grant)), [state.grant])

  return {
    list: state.list,
    selected,
    grantedName: state.grant?.name ?? null,
    cashier,
    cashierNow,
    blockReason: cashierBlockReason({ list: state.list, selected, grant: state.grant, expired: state.expired }),
    verifying: state.verifying,
    problem: state.problem,
    select,
    verify,
    reload,
    forget,
  }
}
